import React, { useState, Component } from 'react';
import { SEO } from '../components/SEO';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { buildBreadcrumbList, buildFAQSchema, buildServiceSchema } from '../lib/structuredData';
import { ArrowRightIcon, SearchIcon, ZapIcon, FileTextIcon, MapPinIcon, TrendingUpIcon, ImageIcon, BrainIcon, GlobeIcon, CheckCircleIcon, BarChartIcon, LinkIcon, CodeIcon, SettingsIcon, LayersIcon, MousePointerClickIcon, DatabaseIcon, WrenchIcon, RocketIcon, RefreshCwIcon, TargetIcon, ChevronDownIcon, ChevronUpIcon, ShieldCheckIcon, Share2Icon, LayoutTemplateIcon, NetworkIcon, SmartphoneIcon, MessageSquareIcon, PlayCircleIcon, AlertTriangleIcon, ChevronRightIcon } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
export const SEOPage: React.FC = () => {
  const [expandedComponent, setExpandedComponent] = useState<string | null>(null);
  const [expandedFaq, setExpandedFaq] = useState<number | null>(null);
  // --- DATA STRUCTURES ---
  const seoFactors = [{
    icon: <WrenchIcon className="w-6 h-6" data-id="element-2178" data-mp="w5il8ffq9t8t" />,
    title: 'Techniek',
    desc: 'De fundering. Als Google je site niet kan lezen, houdt het op.'
  }, {
    icon: <FileTextIcon className="w-6 h-6" data-id="element-2179" data-mp="m8dstxvah1jk" />,
    title: 'Content',
    desc: 'Het antwoord op de vraag van je klant. Relevant en diepgaand.'
  }, {
    icon: <ShieldCheckIcon className="w-6 h-6" data-id="element-2180" data-mp="rabk9fduf2nl" />,
    title: 'Autoriteit',
    desc: 'Vertrouwen. Andere sites die naar jou verwijzen als expert.'
  }, {
    icon: <MousePointerClickIcon className="w-6 h-6" data-id="element-2181" data-mp="i8vifw13g8uz" />,
    title: 'UX',
    desc: 'Gebruiksvriendelijkheid. Een bezoeker moet makkelijk navigeren.'
  }, {
    icon: <Share2Icon className="w-6 h-6" data-id="element-2182" data-mp="8i0lnxq81qva" />,
    title: 'Distributie',
    desc: 'Hoe je content verspreid wordt over verschillende kanalen.'
  }];
  const maturityPhases = [{
    phase: 'Basis',
    levels: '0-2',
    title: 'De Fundering',
    happening: 'Website staat live, maar trekt nauwelijks relevant verkeer. Losse blogs zonder strategie.',
    missing: 'Technische basis, logische structuur en gerichte zoekwoorden.',
    color: 'bg-gray-100 border-gray-200 text-gray-800'
  }, {
    phase: 'Groei',
    levels: '3-5',
    title: 'De Structuur',
    happening: "Verkeer neemt toe. Pagina's ranken op specifieke termen. Leads beginnen binnen te komen.",
    missing: 'Silo-structuur, interne linking en schaalbare content.',
    color: 'bg-blue-50 border-blue-200 text-[#007AFF]'
  }, {
    phase: 'Dominantie',
    levels: '6-9',
    title: 'Het Systeem',
    happening: 'Je bent de autoriteit in jouw niche. Verkeer is voorspelbaar en schaalbaar. Conversies zijn hoog.',
    missing: 'Niets. Het is nu een kwestie van uitbouwen en domineren.',
    color: 'bg-[#1B2B44] border-[#1B2B44] text-white'
  }];
  const seoComponents = [{
    id: 'technisch',
    title: 'Technische SEO',
    icon: <CodeIcon className="w-5 h-5" data-id="element-2183" data-mp="ypon8hks79mq" />,
    explanation: 'De motor van je website. Zonder goede techniek kan Google je content niet vinden of begrijpen.',
    happens: 'We optimaliseren laadtijden (Core Web Vitals), fixen dode links, verbeteren de mobiele weergave en zorgen voor een perfecte indexatie.',
    matters: 'Als de techniek faalt, is alle andere moeite voor niets. Een snelle, foutloze site is een harde eis van Google.',
    blogLink: '/seo/blog/technische-seo-uitleg',
    blogTitle: 'Wat is technische SEO?'
  }, {
    id: 'onpage',
    title: 'On-page SEO',
    icon: <LayoutTemplateIcon className="w-5 h-5" data-id="element-2184" data-mp="3p3zo8newni53" />,
    explanation: "Het optimaliseren van individuele pagina's zodat ze perfect aansluiten bij wat de zoeker wil zien.",
    happens: 'We structureren titels (H1, H2), optimaliseren meta-data, verbeteren de leesbaarheid en zorgen voor de juiste zoekintentie.',
    matters: 'Dit vertelt Google exact waar de pagina over gaat en overtuigt de bezoeker om te klikken in de zoekresultaten.',
    blogLink: '/seo/blog/hoe-optimaliseer-je-een-pagina',
    blogTitle: 'Hoe optimaliseer je een pagina voor Google?'
  }, {
    id: 'architectuur',
    title: 'Site Architectuur',
    icon: <NetworkIcon className="w-5 h-5" data-id="element-2185" data-mp="1dhnrwnxfrskk" />,
    explanation: "De manier waarop je website is opgebouwd en hoe pagina's met elkaar verbonden zijn.",
    happens: 'We bouwen een logische hiërarchie (Silo structuur) en implementeren een slimme interne linkstrategie.',
    matters: 'Een goede structuur verdeelt de "SEO-waarde" over je hele site en helpt bezoekers (en Google) de weg te vinden.',
    blogLink: '/seo/blog/waarom-interne-linking-cruciaal-is',
    blogTitle: 'Waarom interne linking cruciaal is'
  }, {
    id: 'content',
    title: 'Content Strategie',
    icon: <FileTextIcon className="w-5 h-5" data-id="element-2186" data-mp="14gpitr854uv4" />,
    explanation: 'Het plan achter wat je schrijft. Geen losse flodders, maar een doordacht systeem van artikelen.',
    happens: 'We creëren topic clusters: één hoofdpagina (pillar) met daaromheen ondersteunende artikelen die allemaal naar elkaar linken.',
    matters: 'Dit bouwt topical authority op. Je laat zien dat je dé expert bent over een heel onderwerp, niet slechts één zoekwoord.',
    blogLink: '/seo/blog/zoekintentie-uitleg',
    blogTitle: 'Wat is zoekintentie?'
  }, {
    id: 'autoriteit',
    title: 'Autoriteit (Off-page)',
    icon: <LinkIcon className="w-5 h-5" data-id="element-2187" data-mp="28q9511ii374j" />,
    explanation: 'Hoe de rest van het internet naar jouw website kijkt. Ben je een betrouwbare bron?',
    happens: 'We verzamelen kwalitatieve links (backlinks) van andere relevante, betrouwbare websites naar jouw site.',
    matters: 'Backlinks werken als "stemmen". Hoe meer kwalitatieve stemmen, hoe hoger Google je waardeert.',
    blogLink: '/seo/blog/hoe-bouw-je-autoriteit-op',
    blogTitle: 'Hoe bouw je autoriteit op?'
  }, {
    id: 'lokaal',
    title: 'Local SEO',
    icon: <MapPinIcon className="w-5 h-5" data-id="element-2188" data-mp="2wcpj0ktd74m3" />,
    explanation: 'Gevonden worden door mensen in jouw directe omgeving die nu een oplossing zoeken.',
    happens: 'We optimaliseren je Google Bedrijfsprofiel, Apple Business Connect en zorgen voor lokale vermeldingen.',
    matters: 'Voor veel adviseurs komen de beste leads uit de eigen regio. Local SEO zorgt dat jij bovenaan staat op de kaart.',
    blogLink: '/seo/blog/local-seo-uitleg',
    blogTitle: 'Wat is local SEO en hoe word je lokaal vindbaar?'
  }, {
    id: 'ux',
    title: 'UX & Conversie',
    icon: <MousePointerClickIcon className="w-5 h-5" data-id="element-2189" data-mp="38xwedg1noc7s" />,
    explanation: 'De gebruikerservaring. Wat doet een bezoeker zodra hij op je site landt?',
    happens: 'We analyseren gedrag, verbeteren de navigatie, versnellen het proces en plaatsen duidelijke call-to-actions.',
    matters: 'Verkeer zonder conversie is waardeloos. Een goede UX houdt bezoekers langer vast (goed voor SEO) en levert leads op.',
    blogLink: '/seo/blog/wat-is-ux-en-waarom-beinvloedt-het-seo',
    blogTitle: 'Wat is UX en waarom beïnvloedt het SEO?'
  }, {
    id: 'ai',
    title: 'Entity & AI SEO',
    icon: <BrainIcon className="w-5 h-5" data-id="element-2190" data-mp="o75gab9s8xma" />,
    explanation: 'Optimaliseren voor hoe moderne (AI) zoekmachines de wereld begrijpen in concepten, niet alleen in woorden.',
    happens: 'We gebruiken structured data (Schema markup) om entiteiten (personen, bedrijven, diensten) keihard te definiëren voor Google.',
    matters: 'Zoekmachines worden slimmer. Ze zoeken naar de betekenis achter woorden. Wie dit goed doet, domineert de toekomst.',
    blogLink: '/seo/blog/wat-is-entity-seo',
    blogTitle: 'Wat is entity SEO?'
  }, {
    id: 'distributie',
    title: 'Distributie',
    icon: <Share2Icon className="w-5 h-5" data-id="element-2191" data-mp="dreip14k3m2u" />,
    explanation: 'Hoe je content bij de juiste mensen krijgt, buiten Google om.',
    happens: 'We zorgen dat content makkelijk deelbaar is en hergebruikt kan worden voor nieuwsbrieven en social media.',
    matters: 'SEO is traag, distributie is snel. Samen zorgen ze voor een constante stroom van bezoekers.',
    blogLink: '/seo/blog/seo-vs-advertenties',
    blogTitle: 'SEO vs advertenties: wat is het verschil?'
  }, {
    id: 'visueel',
    title: 'Visual & Media SEO',
    icon: <ImageIcon className="w-5 h-5" data-id="element-2192" data-mp="2o7ivnpx4u9wb" />,
    explanation: "Het vindbaar maken van je afbeeldingen, video's en documenten.",
    happens: "We optimaliseren bestandsnamen, alt-teksten, compressie en video-schema's.",
    matters: "Mensen zoeken steeds vaker visueel. Bovendien maken geoptimaliseerde media je gewone pagina's sterker.",
    blogLink: '/seo/blog/wat-is-technische-seo',
    blogTitle: 'Wat is technische SEO?'
  }, {
    id: 'data',
    title: 'Data & Optimalisatie',
    icon: <BarChartIcon className="w-5 h-5" data-id="element-2193" data-mp="32wx6l2gcwzdt" />,
    explanation: 'Meten is weten. We sturen op harde data, niet op onderbuikgevoel.',
    happens: 'We monitoren rankings, klikfrequenties (CTR) en conversies via Google Search Console en Analytics.',
    matters: 'Zonder data ben je blind. Door te meten kunnen we continu bijsturen en het systeem verbeteren.',
    blogLink: '/seo/blog/hoe-lang-duurt-seo',
    blogTitle: 'Hoe lang duurt SEO voordat je resultaat ziet?'
  }, {
    id: 'trust',
    title: 'Trust & Legal',
    icon: <ShieldCheckIcon className="w-5 h-5" data-id="element-2194" data-mp="3t6fjqa0asxwo" />,
    explanation: 'E-E-A-T: Experience, Expertise, Authoritativeness, Trustworthiness.',
    happens: 'We voegen auteursprofielen toe, tonen keurmerken, reviews en zorgen voor transparante voorwaarden.',
    matters: 'Zeker in de financiële sector (YMYL, oftewel Your Money or Your Life) eist Google extreme betrouwbaarheid voordat ze je tonen.',
    blogLink: '/seo/blog/waarom-werkt-seo-vaak-niet',
    blogTitle: 'Waarom werkt SEO vaak niet?'
  }];
  const formulas = [{
    title: 'De Ranking Formule',
    formula: 'Ranking = Relevantie × Autoriteit × Vertrouwen'
  }, {
    title: 'De Groei Formule',
    formula: 'SEO groei = (Content × Structuur) × Tijd'
  }, {
    title: 'De Traffic Formule',
    formula: 'Traffic = Ranking × CTR'
  }, {
    title: 'De Conversie Formule',
    formula: 'Conversie = Traffic × Vertrouwen × Duidelijkheid'
  }, {
    title: 'De Dominantie Formule',
    formula: 'Dominantie = Silo × Content × Distributie × Tijd'
  }];
  const faqs = [{
    q: 'Wat is SEO precies?',
    a: 'SEO (Search Engine Optimization) is het proces van het optimaliseren van je website zodat deze beter vindbaar is in zoekmachines zoals Google. Het is geen eenmalig trucje, maar een systeem van techniek, content, autoriteit en gebruikerservaring dat samenwerkt om structureel relevante bezoekers aan te trekken.'
  }, {
    q: 'Hoe lang duurt SEO?',
    a: 'SEO is een lange-termijn strategie. Meestal zie je de eerste voorzichtige resultaten na 3 tot 6 maanden. Echte, structurele groei en dominantie in jouw markt kost vaak 6 tot 12 maanden. Dit hangt af van de concurrentie, de huidige staat van je website en de inzet die we leveren. Het is een investering die zich op de lange termijn exponentieel terugbetaalt.'
  }, {
    q: 'Waarom werkt SEO vaak niet?',
    a: 'SEO faalt meestal door een gebrek aan structuur of focus. Veel bedrijven schrijven losse blogs zonder onderlinge samenhang (geen silo-structuur), hebben een trage website, of richten zich op zoekwoorden waar hun doelgroep niet op zoekt. SEO werkt alleen als alle factoren (Techniek, Content, Autoriteit, UX) kloppen. Als één factor nul is, is het resultaat nul.'
  }, {
    q: 'Wat is een SEO silo?',
    a: 'Een SEO silo is een manier om je website logisch te structureren. In plaats van alle pagina\'s los naast elkaar te zetten, groepeer je gerelateerde content. Je hebt een hoofdpagina (bijv. "Hypotheekadvies") met daaronder subpagina\'s (bijv. "Hypotheek oversluiten", "Startershypotheek"). Deze linken strategisch naar elkaar. Dit maakt je site overzichtelijk voor bezoekers én Google begrijpt direct waar je expert in bent.'
  }, {
    q: 'Heb ik blogs nodig?',
    a: 'Ja, maar niet zomaar "blogs". Je hebt strategische content nodig die antwoord geeft op de vragen van je doelgroep. We noemen dit liever een kennisbank of topic clusters. Deze informatieve artikelen trekken mensen aan in de oriëntatiefase en bouwen jouw autoriteit op. Vervolgens leiden we deze bezoekers via interne links naar je commerciële dienstpagina\'s.'
  }, {
    q: 'Is SEO beter dan advertenties?',
    a: 'Ze dienen een ander doel. Advertenties (SEA) leveren direct verkeer op, maar zodra je stopt met betalen, stopt het verkeer. SEO duurt langer om op te bouwen, maar levert daarna structureel, "gratis" verkeer op. Voor duurzame groei is SEO essentieel. Vaak is een combinatie in het begin het sterkst: advertenties voor de korte termijn, SEO voor de lange termijn.'
  }, {
    q: 'Wat kost SEO?',
    a: 'SEO is geen standaard pakketje met een vaste prijs. De investering hangt af van jouw doelen, de huidige staat van je website en de concurrentie in jouw markt. We kijken eerst naar wat er nodig is om jouw doelen te bereiken en maken op basis daarvan een strategisch plan. Zie het niet als een kostenpost, maar als een investering in een structureel verkoopkanaal.'
  }];
  const blogClusters = [{
    title: 'SEO Fundamentals',
    articles: [{
      title: 'Wat is SEO en hoe werkt het?',
      slug: 'wat-is-seo-en-hoe-werkt-het'
    }, {
      title: 'Waarom werkt SEO vaak niet?',
      slug: 'waarom-werkt-seo-vaak-niet'
    }, {
      title: 'Hoe lang duurt SEO voordat je resultaat ziet?',
      slug: 'hoe-lang-duurt-seo'
    }, {
      title: 'SEO vs advertenties: wat is het verschil?',
      slug: 'seo-vs-advertenties'
    }]
  }, {
    title: 'SEO Silo (Core)',
    articles: [{
      title: 'Wat is een SEO silo en hoe werkt het?',
      slug: 'seo-silo-wat-is-het'
    }, {
      title: 'Hoe bouw je een SEO silo structuur?',
      slug: 'seo-silo-opzetten'
    }, {
      title: 'Waarom interne linking cruciaal is',
      slug: 'waarom-interne-linking-cruciaal-is'
    }, {
      title: 'Veelgemaakte fouten in website structuur',
      slug: 'veelgemaakte-fouten-website-structuur'
    }, {
      title: 'Hoe Google websites begrijpt (simpel uitgelegd)',
      slug: 'hoe-google-websites-begrijpt'
    }]
  }, {
    title: 'Technical SEO',
    articles: [{
      title: 'Wat is technische SEO?',
      slug: 'technische-seo-uitleg'
    }, {
      title: 'Wat zijn Core Web Vitals?',
      slug: 'wat-zijn-core-web-vitals'
    }, {
      title: 'Hoe werkt indexatie in Google?',
      slug: 'hoe-werkt-indexatie'
    }, {
      title: 'Wat doet een XML sitemap?',
      slug: 'wat-doet-een-xml-sitemap'
    }, {
      title: 'Wat zijn canonicals en waarom zijn ze belangrijk?',
      slug: 'wat-zijn-canonicals'
    }]
  }, {
    title: 'Content SEO',
    articles: [{
      title: 'Hoe schrijf je SEO teksten?',
      slug: 'hoe-schrijf-je-seo-teksten'
    }, {
      title: 'Wat is zoekintentie?',
      slug: 'zoekintentie-uitleg'
    }, {
      title: 'Hoe kies je de juiste keywords?',
      slug: 'hoe-kies-je-keywords'
    }, {
      title: 'Wat is het verschil tussen blogs en landingspagina’s?',
      slug: 'blogs-vs-landingspaginas'
    }, {
      title: 'Hoe optimaliseer je een pagina voor Google?',
      slug: 'hoe-optimaliseer-je-een-pagina'
    }]
  }, {
    title: 'Local SEO',
    articles: [{
      title: 'Wat is local SEO?',
      slug: 'local-seo-uitleg'
    }, {
      title: 'Hoe werkt Google Business?',
      slug: 'hoe-werkt-google-business'
    }, {
      title: 'Wat is Apple Business Connect?',
      slug: 'wat-is-apple-business-connect'
    }, {
      title: 'Hoe word je lokaal vindbaar in jouw regio?',
      slug: 'lokaal-vindbaar-worden'
    }]
  }, {
    title: 'Authority & Backlinks',
    articles: [{
      title: 'Wat zijn backlinks?',
      slug: 'wat-zijn-backlinks'
    }, {
      title: 'Hoe bouw je autoriteit op?',
      slug: 'hoe-bouw-je-autoriteit-op'
    }, {
      title: 'Wat is het verschil tussen goede en slechte links?',
      slug: 'goede-vs-slechte-links'
    }]
  }, {
    title: 'Programmatic SEO',
    articles: [{
      title: 'Wat is programmatic SEO?',
      slug: 'wat-is-programmatic-seo'
    }, {
      title: 'Hoe schaal je naar 100+ pagina’s?',
      slug: 'hoe-schaal-je-paginas'
    }, {
      title: 'Hoe voorkom je duplicate content?',
      slug: 'voorkom-duplicate-content'
    }]
  }, {
    title: 'AI & Entity SEO',
    articles: [{
      title: 'Wat is entity SEO?',
      slug: 'wat-is-entity-seo'
    }, {
      title: 'Hoe werkt SEO voor AI tools zoals ChatGPT?',
      slug: 'seo-voor-ai-tools'
    }, {
      title: 'Wat is structured data?',
      slug: 'wat-is-structured-data'
    }]
  }, {
    title: 'UX & Conversie',
    articles: [{
      title: 'Wat is UX en waarom beïnvloedt het SEO?',
      slug: 'wat-is-ux-en-waarom-beinvloedt-het-seo'
    }, {
      title: 'Hoe verhoog je conversie op je website?',
      slug: 'hoe-verhoog-je-conversie'
    }, {
      title: 'Waarom verlaten bezoekers je website?',
      slug: 'waarom-verlaten-bezoekers-website'
    }]
  }];
  return <div className="w-full min-h-screen bg-white text-[#111827] font-sans" data-id="element-2195" data-mp="3rmryip4fcbbn">
      <SEO title="SEO in Noord-Holland Noord, structureel beter gevonden | Noord Digital" description="SEO is geen los trucje maar een systeem van techniek, content en autoriteit. Zo zorgen we dat ondernemers in Noord-Holland Noord gevonden worden op wat hun klanten echt zoeken." keywords="seo Noord-Holland Noord, zoekmachine optimalisatie Alkmaar, lokale seo, beter gevonden in Google, seo voor mkb, silo-structuur seo" canonical="https://noorddigital.nl/seo" jsonLd={[buildServiceSchema({
      name: 'SEO',
      description: 'Zoekmachine optimalisatie voor dienstverlenende bedrijven in Noord-Holland Noord, gericht op de zoekvragen die echt aanvragen opleveren.',
      path: '/seo',
      serviceType: 'Zoekmachine optimalisatie'
    }), buildBreadcrumbList([{
      name: 'SEO',
      path: '/seo'
    }]), buildFAQSchema(faqs.map(faq => ({
      question: faq.q,
      answer: faq.a
    })))]} data-id="element-2196" data-mp="ffgk7vpvtuo" />
      <Header data-id="element-2197" data-mp="2mnldtylpkhwm" />

      {/* 1. HERO SECTION */}
      <section className="w-full bg-gradient-to-br from-[#007AFF] to-[#1B2B44] py-20 md:py-28 relative overflow-hidden" data-id="element-2198" data-mp="2moeohansfrk1">
        <div className="absolute inset-0 opacity-10" data-id="element-2199" data-mp="2inuq63rhahwb">
          <div className="w-full h-full bg-[url('https://images.unsplash.com/photo-1460925895917-afdab827c52f?ixlib=rb-4.0.3&auto=format&fit=crop&w=1470&q=80')] bg-cover bg-center" data-id="element-2200" data-mp="lk2pnx2g6e3v"></div>
        </div>

        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-[1200px] relative z-10" data-id="element-2201" data-mp="clha8as9vf7b">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center" data-id="element-2202" data-mp="hkagpj9565xz">
            <div className="lg:col-span-7" data-id="element-2203" data-mp="158l7tup1cr92">
              <motion.div initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.6
            }} className="inline-flex items-center gap-3 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full border border-white/20 mb-8" data-id="element-2204" data-mp="159mtsu7lay5k">
                <img src="https://cdn.magicpatterns.com/uploads/fv5nFyNt1SPvWUkxd2YKYJ/Norden_portret_300dpi_2.png" alt="Norden" className="w-8 h-8 rounded-full border border-white/30" data-id="element-2205" data-mp="1o75zpoyr40nr" />
                <span className="text-white/90 text-sm font-medium" data-id="element-2206" data-mp="22czj2lrup8vb">
                  Norden helpt je door de wirwar van SEO heen
                </span>
              </motion.div>

              <motion.h1 initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.6,
              delay: 0.1
            }} className="font-bold text-white leading-tight mb-6 text-4xl md:text-5xl lg:text-6xl" data-id="element-2207" data-mp="1y9tb99p3b9mq">
                SEO dat werkt als systeem, niet als trucje
              </motion.h1>

              <motion.p initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.6,
              delay: 0.2
            }} className="text-white/90 mb-10 text-xl leading-relaxed font-medium max-w-2xl" data-id="element-2208" data-mp="1td2ns1ayhbra">
                De meeste websites zijn onzichtbaar omdat de structuur
                ontbreekt. Wij bouwen geen losse blogs, we bouwen een schaalbaar
                systeem voor structurele groei. Voor ondernemers in{' '}
                <a href="/werkgebied/alkmaar" className="underline underline-offset-4 decoration-white/50 hover:decoration-white transition-colors duration-200" data-id="element-2209" data-mp="3v039dt5dnedg">Alkmaar</a>{' '}
                en de rest van Noord-Holland Noord.
              </motion.p>

              <motion.div initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.6,
              delay: 0.3
            }} className="flex flex-wrap gap-4" data-id="element-2210" data-mp="mq1kufvmew40">
                <a href="/prijsscan" className="inline-flex items-center px-8 py-4 bg-white text-[#007AFF] font-bold rounded-lg hover:bg-gray-50 transition duration-300 shadow-lg group" data-id="element-2211" data-mp="rh05frnn6gd2">
                  Start de scan
                  <ArrowRightIcon className="h-5 w-5 ml-2 group-hover:translate-x-1 transition-transform" data-id="element-2212" data-mp="10j9fkgf007px" />
                </a>
                <a href="/#contact" className="inline-flex items-center px-8 py-4 bg-transparent border-2 border-white/30 text-white font-bold rounded-lg hover:bg-white/10 transition duration-300" data-id="element-2213" data-mp="1k4t4i31q37pe">
                  Plan een gesprek
                </a>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* 2. WHAT SEO REALLY IS */}
      <section className="py-20 bg-white" data-id="element-2214" data-mp="11ch1rf75q4eq">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-[1200px]" data-id="element-2215" data-mp="11bp8ib118irm">
          <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-2216" data-mp="165jqna0zopol">
            <h2 className="text-3xl md:text-4xl font-bold text-[#111827] mb-6" data-id="element-2217" data-mp="rgds7o9z73hr">
              De harde waarheid over SEO
            </h2>
            <p className="text-lg text-[#6B7280] leading-relaxed" data-id="element-2218" data-mp="wccmfshzjfq3">
              SEO is geen losse dienst die je even "aanzet". Het is een
              vermenigvuldiging van factoren. Als één van deze factoren een nul
              is, is het eindresultaat ook nul.
            </p>
          </div>

          <div className="bg-[#F7F8FA] rounded-2xl p-8 md:p-12 border border-gray-100 mb-12" data-id="element-2219" data-mp="2p4zr1dfnl5wx">
            <div className="flex flex-col md:flex-row items-center justify-center gap-4 md:gap-8 text-xl md:text-2xl font-bold text-[#1B2B44] text-center flex-wrap" data-id="element-2220" data-mp="2kavjot2h6vvb">
              <span data-id="element-2221" data-mp="2bcjhruw5sbnk">Zichtbaarheid</span>
              <span className="text-[#007AFF]" data-id="element-2222" data-mp="2b9f9albnutr3">=</span>
              <span className="flex items-center gap-2" data-id="element-2223" data-mp="380pu73zbo8ce">
                <WrenchIcon className="w-6 h-6 text-[#007AFF]" data-id="element-2224" data-mp="332ocamdmuf2i" /> Techniek
              </span>
              <span className="text-[#007AFF]" data-id="element-2225" data-mp="330v4v4v73qtg">×</span>
              <span className="flex items-center gap-2" data-id="element-2226" data-mp="2yzromh98jsrp">
                <FileTextIcon className="w-6 h-6 text-[#007AFF]" data-id="element-2227" data-mp="2u29p1hh3ypwd" /> Content
              </span>
              <span className="text-[#007AFF]" data-id="element-2228" data-mp="1nlxo58mwitx3">×</span>
              <span className="flex items-center gap-2" data-id="element-2229" data-mp="1nnij2zd4oevh">
                <ShieldCheckIcon className="w-6 h-6 text-[#007AFF]" data-id="element-2230" data-mp="1imbaiy35vbnr" />{' '}
                Autoriteit
              </span>
              <span className="text-[#007AFF]" data-id="element-2231" data-mp="3ub918nlsq428">×</span>
              <span className="flex items-center gap-2" data-id="element-2232" data-mp="33wk28kwxqmb">
                <MousePointerClickIcon className="w-6 h-6 text-[#007AFF]" data-id="element-2233" data-mp="34oe3eyv3gah" /> UX
              </span>
              <span className="text-[#007AFF]" data-id="element-2234" data-mp="74owtbszudnc">×</span>
              <span className="flex items-center gap-2" data-id="element-2235" data-mp="bvyjq4n5r2vj">
                <Share2Icon className="w-6 h-6 text-[#007AFF]" data-id="element-2236" data-mp="bxi0dwcq7fxu" /> Distributie
              </span>
            </div>
            <div className="mt-8 flex items-center justify-center gap-3" data-id="element-2237" data-mp="gxe743ct5lh3">
              <img src="https://cdn.magicpatterns.com/uploads/fv5nFyNt1SPvWUkxd2YKYJ/Norden_portret_300dpi_2.png" alt="Norden" className="w-6 h-6 rounded-full border border-gray-300" data-id="element-2238" data-mp="lstgahzvet76" />
              <p className="text-sm italic text-[#6B7280]" data-id="element-2239" data-mp="utrlv4pyj8rv">
                Norden legt het simpel uit: als één factor ontbreekt, valt het
                hele systeem.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-6" data-id="element-2240" data-mp="2wmpkjh6f0vkq">
            {seoFactors.map((factor, idx) => <div key={idx} className="bg-white p-6 rounded-xl border border-gray-100 shadow-sm hover:shadow-md transition-shadow" data-id="element-2241" data-mp="21ec4ee4d6vjz" data-mpi={idx}>
                <div className="w-12 h-12 bg-blue-50 rounded-lg flex items-center justify-center text-[#007AFF] mb-4" data-id="element-2242" data-mp="2bayrl9k5xyp6">
                  {factor.icon}
                </div>
                <h3 className="font-bold text-[#111827] mb-2" data-id="element-2243" data-mp="269j7bjpt7w7u">
                  {factor.title}
                </h3>
                <p className="text-sm text-[#6B7280] leading-relaxed" data-id="element-2244" data-mp="1dmyfyj9c3k4a">
                  {factor.desc}
                </p>
              </div>)}
          </div>
        </div>
      </section>

      {/* 3. SEO MATURITY MODEL */}
      <section className="py-20 bg-[#F7F8FA]" data-id="element-2245" data-mp="1nkkzjj1kvzul">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-[1200px]" data-id="element-2246" data-mp="1nirozsnugvhb">
          <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-2247" data-mp="ztp5ef9ngsk6">
            <h2 className="text-3xl md:text-4xl font-bold text-[#111827] mb-6" data-id="element-2248" data-mp="uwys1zp8cb2g">
              Het SEO Volwassenheidsmodel
            </h2>
            <p className="text-lg text-[#6B7280] leading-relaxed" data-id="element-2249" data-mp="36i7rc1m2jlsa">
              Groei in SEO gaat in fases. Je kunt geen dominantie verwachten als
              de fundering nog ontbreekt. Waar staat jouw kantoor?
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-2250" data-mp="31jxt1i51s52g">
            {maturityPhases.map((phase, idx) => <div key={idx} className={`rounded-2xl p-8 border-2 relative overflow-hidden ${phase.color}`} data-id="element-2251" data-mp="3p9ih2fg25sil" data-mpi={idx}>
                <div className="absolute top-0 right-0 p-4 opacity-20 font-black text-6xl" data-id="element-2252" data-mp="3u9d242d5jnvq">
                  {phase.levels}
                </div>
                <div className="relative z-10" data-id="element-2253" data-mp="3gbg5j5yp7hh2">
                  <span className="uppercase tracking-wider text-xs font-bold mb-2 block opacity-80" data-id="element-2254" data-mp="3l7ndt5ekh526">
                    Fase {idx + 1}: {phase.phase}
                  </span>
                  <h3 className="text-2xl font-bold mb-6" data-id="element-2255" data-mp="29t04mq9pr6ms">{phase.title}</h3>

                  <div className="space-y-6" data-id="element-2256" data-mp="29qx3pb70vt3a">
                    <div data-id="element-2257" data-mp="fc00ux4xti15">
                      <h4 className="font-semibold mb-2 flex items-center gap-2 opacity-90" data-id="element-2258" data-mp="fcjgu8b11wcj">
                        <CheckCircleIcon className="w-4 h-4" data-id="element-2259" data-mp="aei2u1sgkn7x" /> Wat gebeurt er
                        nu?
                      </h4>
                      <p className="text-sm opacity-80 leading-relaxed" data-id="element-2260" data-mp="5kvn20r0037w">
                        {phase.happening}
                      </p>
                    </div>
                    <div data-id="element-2261" data-mp="3stivj0hsfnai">
                      <h4 className="font-semibold mb-2 flex items-center gap-2 opacity-90" data-id="element-2262" data-mp="3spl8w4w04dlv">
                        <ZapIcon className="w-4 h-4" data-id="element-2263" data-mp="3nuxuiyupozl0" /> Wat ontbreekt er?
                      </h4>
                      <p className="text-sm opacity-80 leading-relaxed" data-id="element-2264" data-mp="3ivupheeqetd1">
                        {phase.missing}
                      </p>
                    </div>
                  </div>
                </div>
              </div>)}
          </div>
        </div>
      </section>

      {/* 3.5 WAAROM SEO VAAK NIET WERKT */}
      <section className="py-24 bg-white" data-id="element-2265" data-mp="2uyg96k9yctyt">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-[1200px]" data-id="element-2266" data-mp="2uxdf63hc2beo">
          <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-2267" data-mp="2zwq3ytrrlsjw">
            <h2 className="text-3xl md:text-4xl font-bold text-[#111827] mb-6" data-id="element-2268" data-mp="2zv6np363zhi4">
              Waarom SEO bij 90% van de bedrijven niet werkt
            </h2>
            <div className="flex items-center justify-center gap-3 mb-6" data-id="element-2269" data-mp="38ojpyvq2bvgv">
              <img src="https://cdn.magicpatterns.com/uploads/fv5nFyNt1SPvWUkxd2YKYJ/Norden_portret_300dpi_2.png" alt="Norden" className="w-6 h-6 rounded-full border border-gray-300" data-id="element-2270" data-mp="2c0caykvlsfbs" />
              <p className="text-sm italic text-[#6B7280]" data-id="element-2271" data-mp="2l0ijrtgcwr4m">
                Norden ziet het keer op keer: bedrijven investeren in SEO, maar
                missen de structuur om het te laten werken.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12" data-id="element-2272" data-mp="9i7dycswtt9">
            <div className="bg-[#F7F8FA] p-6 rounded-xl border border-gray-100" data-id="element-2273" data-mp="57tgu7z6p2ay">
              <div className="w-10 h-10 bg-red-50 rounded-lg flex items-center justify-center text-red-500 mb-4" data-id="element-2274" data-mp="3d301r8dhc0hg">
                <AlertTriangleIcon className="w-5 h-5" data-id="element-2275" data-mp="38456h6ybsjgg" />
              </div>
              <h3 className="font-bold text-[#111827] mb-2" data-id="element-2276" data-mp="3luif7bj1k26u">
                1. Losse blogs zonder structuur
              </h3>
              <p className="text-sm text-[#6B7280] leading-relaxed" data-id="element-2277" data-mp="3h1z4qnfnhn69">
                Willekeurige artikelen schrijven zonder dat ze onderling
                verbonden zijn. Google snapt de context niet.
              </p>
            </div>
            <div className="bg-[#F7F8FA] p-6 rounded-xl border border-gray-100" data-id="element-2278" data-mp="8nqgfrk5vijw">
              <div className="w-10 h-10 bg-red-50 rounded-lg flex items-center justify-center text-red-500 mb-4" data-id="element-2279" data-mp="8lmdpcy00pl7">
                <TargetIcon className="w-5 h-5" data-id="element-2280" data-mp="2af3lqhq1jsgh" />
              </div>
              <h3 className="font-bold text-[#111827] mb-2" data-id="element-2281" data-mp="2afdda9qqi7oa">
                2. Verkeerde zoekintentie
              </h3>
              <p className="text-sm text-[#6B7280] leading-relaxed" data-id="element-2282" data-mp="26fut5g6qm0mi">
                Proberen te ranken met een verkooppagina op een zoekterm waarbij
                de gebruiker eigenlijk informatie zoekt.
              </p>
            </div>
            <div className="bg-[#F7F8FA] p-6 rounded-xl border border-gray-100" data-id="element-2283" data-mp="1mkjn57cw4zrz">
              <div className="w-10 h-10 bg-red-50 rounded-lg flex items-center justify-center text-red-500 mb-4" data-id="element-2284" data-mp="1ribgm5t6otya">
                <RefreshCwIcon className="w-5 h-5" data-id="element-2285" data-mp="1clax2pl5c5aw" />
              </div>
              <h3 className="font-bold text-[#111827] mb-2" data-id="element-2286" data-mp="1hkf7tknpyb3c">
                3. Te snel opgeven
              </h3>
              <p className="text-sm text-[#6B7280] leading-relaxed" data-id="element-2287" data-mp="13lfgu6cqhk40">
                Stoppen na 3 maanden omdat er nog geen leads zijn, precies
                wanneer het vliegwiel op gang begint te komen.
              </p>
            </div>
            <div className="bg-[#F7F8FA] p-6 rounded-xl border border-gray-100" data-id="element-2288" data-mp="2xzgkd4j8avaf">
              <div className="w-10 h-10 bg-red-50 rounded-lg flex items-center justify-center text-red-500 mb-4" data-id="element-2289" data-mp="32wp1r2wjyvam">
                <CodeIcon className="w-5 h-5" data-id="element-2290" data-mp="8ltfu9p5r4bq" />
              </div>
              <h3 className="font-bold text-[#111827] mb-2" data-id="element-2291" data-mp="dik0797zwm2y">
                4. Techniek zonder content
              </h3>
              <p className="text-sm text-[#6B7280] leading-relaxed" data-id="element-2292" data-mp="igv7pdatiki0">
                Een razendsnelle website hebben, maar geen antwoord geven op de
                vragen van je doelgroep.
              </p>
            </div>
            <div className="bg-[#F7F8FA] p-6 rounded-xl border border-gray-100" data-id="element-2293" data-mp="rcuesxqa2zva">
              <div className="w-10 h-10 bg-red-50 rounded-lg flex items-center justify-center text-red-500 mb-4" data-id="element-2294" data-mp="w89ttt00v1ta">
                <NetworkIcon className="w-5 h-5" data-id="element-2295" data-mp="116urbknw37e1" />
              </div>
              <h3 className="font-bold text-[#111827] mb-2" data-id="element-2296" data-mp="166plpy6vif3u">
                5. Geen interne linking
              </h3>
              <p className="text-sm text-[#6B7280] leading-relaxed" data-id="element-2297" data-mp="33254u1et6ofq">
                Pagina's die nergens naar verwijzen. De SEO-waarde stroomt niet
                door de website heen.
              </p>
            </div>
          </div>

          <div className="text-center" data-id="element-2298" data-mp="1kdststkxrj5d">
            <a href="/seo/blog/waarom-werkt-seo-vaak-niet" className="inline-flex items-center text-[#007AFF] font-semibold hover:text-[#005FCC] transition-colors group" data-id="element-2299" data-mp="1kcr9dyi15kaz">
              Lees de uitgebreide analyse waarom SEO vaak faalt
              <ArrowRightIcon className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" data-id="element-2300" data-mp="1bb1gspcewfbm" />
            </a>
          </div>
        </div>
      </section>

      {/* 4. SEO SILO (MOST IMPORTANT) */}
      <section className="py-32 bg-[#1B2B44] text-white relative overflow-hidden" data-id="element-2301" data-mp="1z2gn0t7b7gs5">
        {/* Decorative background lines */}
        <div className="absolute inset-0 opacity-5" data-id="element-2302" data-mp="1u45c0f5a308c">
          <div className="absolute top-0 left-1/4 w-px h-full bg-white" data-id="element-2303" data-mp="1u5gf0cqwg3ye"></div>
          <div className="absolute top-0 left-2/4 w-px h-full bg-white" data-id="element-2304" data-mp="noux35tmlb5k"></div>
          <div className="absolute top-0 left-3/4 w-px h-full bg-white" data-id="element-2305" data-mp="in5k3kmueneu"></div>
        </div>

        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-[1200px] relative z-10" data-id="element-2306" data-mp="k54a6csl9vlg">
          <div className="text-center max-w-3xl mx-auto mb-20" data-id="element-2307" data-mp="p4p8qm3uk1xn">
            <div className="inline-flex items-center gap-3 bg-white/10 px-4 py-2 rounded-full border border-white/20 mb-6" data-id="element-2308" data-mp="p22zmi1b5edb">
              <img src="https://cdn.magicpatterns.com/uploads/fv5nFyNt1SPvWUkxd2YKYJ/Norden_portret_300dpi_2.png" alt="Norden" className="w-6 h-6 rounded-full" data-id="element-2309" data-mp="g398362u3e6m" />
              <span className="text-white text-sm font-bold tracking-wide uppercase" data-id="element-2310" data-mp="1cs0f4zlvs1a">
                Onze kernstrategie
              </span>
            </div>

            <h2 className="text-4xl md:text-5xl font-bold mb-8 leading-tight" data-id="element-2311" data-mp="3oklz0uw546hj">
              De SEO Silo: Waarom structuur alles bepaalt
            </h2>

            <p className="text-xl text-white/80 leading-relaxed mb-8" data-id="element-2312" data-mp="3elehrwmabrp5">
              Wij bouwen een <strong data-id="element-2313" data-mp="3enqyk6gb3w0m">SEO Silo structuur</strong>. We groeperen
              gerelateerde content in strakke hiërarchieën (Home → Categorie →
              Detail). Door slimme interne linking vloeit de SEO-waarde door je
              hele site. Dit is het geheim achter schaalbare groei en ware
              autoriteit.
            </p>

            <div className="flex items-center justify-center gap-3 mb-8" data-id="element-2314" data-mp="39x5da8u6l7xj">
              <img src="https://cdn.magicpatterns.com/uploads/fv5nFyNt1SPvWUkxd2YKYJ/Norden_portret_300dpi_2.png" alt="Norden" className="w-8 h-8 rounded-full border-2 border-white/30" data-id="element-2315" data-mp="34xsj130zcykx" />
              <p className="text-base italic text-white/70" data-id="element-2316" data-mp="3iv7aedasngfj">
                "Norden helpt hier structuur aan te brengen. Dit is waar de
                meeste websites falen, en waar wij het verschil maken."
              </p>
            </div>

            <a href="/seo/blog/seo-silo-wat-is-het" className="inline-flex items-center bg-[#007AFF] text-white px-8 py-4 rounded-lg font-bold hover:bg-[#005FCC] transition-colors group" data-id="element-2317" data-mp="3npbgyvg5dmg3">
              Lees alles over de SEO Silo structuur
              <ArrowRightIcon className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" data-id="element-2318" data-mp="1ez70unuohsv" />
            </a>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center mb-20" data-id="element-2319" data-mp="1zycbuywfhg4y">
            {/* Zonder Silo */}
            <div className="bg-white/5 border border-white/10 rounded-2xl p-8" data-id="element-2320" data-mp="24qnc62v2z3y1">
              <h3 className="text-2xl font-bold mb-6 text-red-400 flex items-center gap-3" data-id="element-2321" data-mp="29p6zo7pbonsi">
                <AlertTriangleIcon className="w-6 h-6" data-id="element-2322" data-mp="1c16n9f6o656k" /> Zonder Silo (Chaos)
              </h3>
              <ul className="space-y-4 text-white/80" data-id="element-2323" data-mp="1gzrnvfpu2qvj">
                <li className="flex items-start gap-3" data-id="element-2324" data-mp="1h09th2ydyhg4">
                  <span className="text-red-400 mt-1" data-id="element-2325" data-mp="1lyl074tdwbu4">✗</span>
                  Pagina's staan los van elkaar, Google snapt de context niet.
                </li>
                <li className="flex items-start gap-3" data-id="element-2326" data-mp="1pyd2x0w3jnrk">
                  <span className="text-red-400 mt-1" data-id="element-2327" data-mp="2xd85h8v49q48">✗</span>
                  SEO-waarde (link juice) stroomt niet door de website.
                </li>
                <li className="flex items-start gap-3" data-id="element-2328" data-mp="pshzz6jqdvj8">
                  <span className="text-red-400 mt-1" data-id="element-2329" data-mp="przvjmmx0b08">✗</span>
                  Bezoekers verdwalen en verlaten de site snel.
                </li>
                <li className="flex items-start gap-3" data-id="element-2330" data-mp="h07mngdjh9xr">
                  <span className="text-red-400 mt-1" data-id="element-2331" data-mp="gymvm0qfo94n">✗</span>
                  Je concurreert met je eigen pagina's (keyword
                  cannibalization).
                </li>
              </ul>
            </div>

            {/* Met Silo */}
            <div className="bg-gradient-to-br from-[#007AFF]/20 to-transparent border border-[#007AFF]/30 rounded-2xl p-8" data-id="element-2332" data-mp="236ujbrfrcyr">
              <h3 className="text-2xl font-bold mb-6 text-[#007AFF] flex items-center gap-3" data-id="element-2333" data-mp="3u7r97ozryn0f">
                <CheckCircleIcon className="w-6 h-6" data-id="element-2334" data-mp="3p8epve843y2l" /> Met Silo (Groei)
              </h3>
              <ul className="space-y-4 text-white/90" data-id="element-2335" data-mp="1sfwbyee9ml1q">
                <li className="flex items-start gap-3" data-id="element-2336" data-mp="1sda4sfvae7k1">
                  <span className="text-[#007AFF] mt-1" data-id="element-2337" data-mp="1nhbaycr6vxc8">✓</span>
                  Strakke hiërarchie: Google ziet jou als dé autoriteit.
                </li>
                <li className="flex items-start gap-3" data-id="element-2338" data-mp="2bblevm8cnuop">
                  <span className="text-[#007AFF] mt-1" data-id="element-2339" data-mp="2bcemepf4uo7w">✓</span>
                  Eén sterke pagina trekt de hele cluster mee omhoog.
                </li>
                <li className="flex items-start gap-3" data-id="element-2340" data-mp="22j1o17yauuef">
                  <span className="text-[#007AFF] mt-1" data-id="element-2341" data-mp="22jjhjwtkuuin">✓</span>
                  Logische navigatie houdt bezoekers langer vast.
                </li>
                <li className="flex items-start gap-3" data-id="element-2342" data-mp="2uazeajoulxml">
                  <span className="text-[#007AFF] mt-1" data-id="element-2343" data-mp="2u8d6qk1yji4d">✓</span>
                  Schaalbaar: nieuwe content voegt direct waarde toe.
                </li>
              </ul>
            </div>
          </div>

          <div className="bg-white/5 rounded-3xl p-4 sm:p-6 md:p-10 border border-white/10 max-w-4xl mx-auto" data-id="element-2344" data-mp="2lbvsv927l64x">
            <div className="flex flex-col items-center" data-id="element-2345" data-mp="14xvsxy3d5gho">
              {/* Home */}
              <div className="bg-[#007AFF] text-white px-6 sm:px-10 py-3 sm:py-4 rounded-xl font-bold text-base sm:text-xl mb-8 relative z-10 shadow-lg border border-white/20" data-id="element-2346" data-mp="19sjcdijifkpe">
                Home
              </div>

              {/* Lines from Home to Categories */}
              <div className="w-full max-w-2xl h-px bg-white/20 relative -mt-4 mb-8 z-0" data-id="element-2347" data-mp="1bai229lcgqvh">
                <div className="absolute left-1/2 top-0 w-px h-8 bg-white/20 -translate-y-full" data-id="element-2348" data-mp="16drhpa2ib949"></div>
                <div className="absolute left-0 top-0 w-px h-8 bg-white/20" data-id="element-2349" data-mp="inx7z1dhi0o8"></div>
                <div className="absolute left-1/2 top-0 w-px h-8 bg-white/20" data-id="element-2350" data-mp="embp8ze9qs64"></div>
                <div className="absolute right-0 top-0 w-px h-8 bg-white/20" data-id="element-2351" data-mp="sdgsebgui2l7"></div>
              </div>

              {/* Categories */}
              <div className="w-full max-w-3xl flex justify-between px-0 sm:px-2 md:px-4 mb-12 relative z-10" data-id="element-2352" data-mp="nlf9w7t33fmq">
                <div className="bg-white text-[#1B2B44] px-2 py-2 sm:px-4 sm:py-2.5 md:px-6 md:py-3 rounded-lg font-bold text-xs sm:text-sm md:text-base shadow-md" data-id="element-2353" data-mp="3w07d0bixfsiw">
                  Hypotheken
                </div>
                <div className="bg-white text-[#1B2B44] px-2 py-2 sm:px-4 sm:py-2.5 md:px-6 md:py-3 rounded-lg font-bold text-xs sm:text-sm md:text-base shadow-md" data-id="element-2354" data-mp="36j6unwoc3ael">
                  Pensioenen
                </div>
                <div className="bg-white text-[#1B2B44] px-2 py-2 sm:px-4 sm:py-2.5 md:px-6 md:py-3 rounded-lg font-bold text-xs sm:text-sm md:text-base shadow-md" data-id="element-2355" data-mp="3kgver2akudil">
                  Verzekeringen
                </div>
              </div>

              {/* Lines from Categories to Details */}
              <div className="w-full max-w-3xl flex justify-between px-3 sm:px-6 md:px-10 relative -mt-6 mb-6 z-0" data-id="element-2356" data-mp="3tf73szvsp38x">
                <div className="w-px h-6 bg-white/20" data-id="element-2357" data-mp="71pa7zdn1oxd"></div>
                <div className="w-px h-6 bg-white/20" data-id="element-2358" data-mp="7178in87iii2"></div>
                <div className="w-px h-6 bg-white/20" data-id="element-2359" data-mp="c1ux728yqn94"></div>
              </div>

              {/* Detail pages (Clusters) */}
              <div className="w-full max-w-3xl flex justify-between px-0 sm:px-2" data-id="element-2360" data-mp="h0o76kp1jw4j">
                <div className="flex flex-col gap-3" data-id="element-2361" data-mp="21iysaiu3jw8v">
                  <div className="bg-white/10 border border-white/20 text-white px-2 py-1.5 sm:px-3 sm:py-2 rounded text-xs sm:text-sm text-center backdrop-blur-sm" data-id="element-2362" data-mp="1wj426hi5baov">
                    Oversluiten
                  </div>
                  <div className="bg-white/10 border border-white/20 text-white px-2 py-1.5 sm:px-3 sm:py-2 rounded text-xs sm:text-sm text-center backdrop-blur-sm" data-id="element-2363" data-mp="1mlpx1e1oa8bv">
                    Starters
                  </div>
                </div>
                <div className="flex flex-col gap-3" data-id="element-2364" data-mp="2fadndo1a3nwa">
                  <div className="bg-white/10 border border-white/20 text-white px-2 py-1.5 sm:px-3 sm:py-2 rounded text-xs sm:text-sm text-center backdrop-blur-sm" data-id="element-2365" data-mp="2aideh638dknk">
                    Lijfrente
                  </div>
                  <div className="bg-white/10 border border-white/20 text-white px-2 py-1.5 sm:px-3 sm:py-2 rounded text-xs sm:text-sm text-center backdrop-blur-sm" data-id="element-2366" data-mp="3307wbuih8sb9">
                    ZZP
                  </div>
                </div>
                <div className="flex flex-col gap-3" data-id="element-2367" data-mp="xj6b88uf5k49">
                  <div className="bg-white/10 border border-white/20 text-white px-2 py-1.5 sm:px-3 sm:py-2 rounded text-xs sm:text-sm text-center backdrop-blur-sm" data-id="element-2368" data-mp="sj1uchplmndi">
                    AOV
                  </div>
                  <div className="bg-white/10 border border-white/20 text-white px-2 py-1.5 sm:px-3 sm:py-2 rounded text-xs sm:text-sm text-center backdrop-blur-sm" data-id="element-2369" data-mp="irfo6zs4behm">
                    Zakelijk
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-20 border-t border-white/10 pt-12" data-id="element-2370" data-mp="4soctl4n46x6">
            <div className="text-center" data-id="element-2371" data-mp="3w0k08jlt9kba">
              <div className="text-4xl font-black text-[#007AFF] mb-2" data-id="element-2372" data-mp="1z6z0vrdeux1w">3x</div>
              <p className="text-white/80 font-medium" data-id="element-2373" data-mp="1u7cofk5epku5">
                Meer organisch verkeer
              </p>
            </div>
            <div className="text-center" data-id="element-2374" data-mp="22p6a91k6efnx">
              <div className="text-4xl font-black text-[#007AFF] mb-2" data-id="element-2375" data-mp="22o4nhztss4qd">Top</div>
              <p className="text-white/80 font-medium" data-id="element-2376" data-mp="26one5qjyf4qz">
                Hogere autoriteit per pagina
              </p>
            </div>
            <div className="text-center" data-id="element-2377" data-mp="2bj14vfno8npb">
              <div className="text-4xl font-black text-[#007AFF] mb-2" data-id="element-2378" data-mp="2gg1cvw4300j2">∞</div>
              <p className="text-white/80 font-medium" data-id="element-2379" data-mp="1jpsb857laeqk">Schaalbare groei</p>
            </div>
          </div>
        </div>
      </section>

      {/* 5. OVERVIEW OF SEO COMPONENTS */}
      <section className="py-20 bg-[#F7F8FA]" data-id="element-2380" data-mp="1nsx9fvkmzu9e">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-[1200px]" data-id="element-2381" data-mp="1sp5kawrsx1ar">
          <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-2382" data-mp="1xpte1q7ezw8o">
            <h2 className="text-3xl md:text-4xl font-bold text-[#111827] mb-6" data-id="element-2383" data-mp="1xmxexyjtz1in">
              De componenten van ons systeem
            </h2>
            <p className="text-lg text-[#6B7280] leading-relaxed mb-6" data-id="element-2384" data-mp="393nljis61vcs">
              Niet alles is altijd nodig. Het hangt af van waar je nu staat in
              je SEO-reis. Hieronder vind je alle onderdelen van ons systeem.
              Klik op een component om te zien wat het inhoudt en waarom het
              belangrijk is.
            </p>
            <div className="flex items-center justify-center gap-3" data-id="element-2385" data-mp="2tc4stflwzg3o">
              <img src="https://cdn.magicpatterns.com/uploads/fv5nFyNt1SPvWUkxd2YKYJ/Norden_portret_300dpi_2.png" alt="Norden" className="w-6 h-6 rounded-full border border-gray-300" data-id="element-2386" data-mp="2ofxkxh9yjuj3" />
              <p className="text-sm italic text-[#6B7280]" data-id="element-2387" data-mp="1vrrymq399xh5">
                Norden helpt je bepalen welke componenten prioriteit hebben.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-2388" data-mp="25omu1048fhr7">
            {seoComponents.map(comp => <div key={comp.id} className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm hover:shadow-md transition-shadow" data-id="element-2389" data-mp="1hyitj1mcby4f">
                <button onClick={() => setExpandedComponent(expandedComponent === comp.id ? null : comp.id)} className="w-full px-6 py-5 flex items-center justify-between text-left focus:outline-none" data-id="element-2390" data-mp="1cy4weema28l6">
                  <div className="flex items-center gap-4" data-id="element-2391" data-mp="3tgdz0jol07yf">
                    <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center text-[#007AFF]" data-id="element-2392" data-mp="3eqg7puvcv250">
                      {comp.icon}
                    </div>
                    <h3 className="font-bold text-[#111827] text-lg" data-id="element-2393" data-mp="b86fz9vcllu5">
                      {comp.title}
                    </h3>
                  </div>
                  {expandedComponent === comp.id ? <ChevronUpIcon className="w-5 h-5 text-gray-400" data-id="element-2394" data-mp="289tnewfkz29" /> : <ChevronDownIcon className="w-5 h-5 text-gray-400" data-id="element-2395" data-mp="27gnog5aqdl6" />}
                </button>

                <AnimatePresence data-id="element-2396" data-mp="680hpaukp133">
                  {expandedComponent === comp.id && <motion.div initial={{
                height: 0,
                opacity: 0
              }} animate={{
                height: 'auto',
                opacity: 1
              }} exit={{
                height: 0,
                opacity: 0
              }} className="overflow-hidden" data-id="element-2397" data-mp="2mxz6jmdkqasb">
                      <div className="px-6 pb-6 pt-2 border-t border-gray-50" data-id="element-2398" data-mp="32f78g9x7j73f">
                        <p className="text-[#111827] font-medium mb-4" data-id="element-2399" data-mp="2xgm5w3uhgbbt">
                          {comp.explanation}
                        </p>

                        <div className="space-y-4 mb-6" data-id="element-2400" data-mp="2sjvs5n65hrtk">
                          <div data-id="element-2401" data-mp="3q62zh4tu9y9m">
                            <span className="text-xs font-bold uppercase tracking-wider text-[#007AFF] block mb-1" data-id="element-2402" data-mp="3q5b49v45m2jv">
                              Wat we doen
                            </span>
                            <p className="text-sm text-[#6B7280] leading-relaxed" data-id="element-2403" data-mp="3g629x15dmlwz">
                              {comp.happens}
                            </p>
                          </div>
                          <div data-id="element-2404" data-mp="24t9k3vw5c7jk">
                            <span className="text-xs font-bold uppercase tracking-wider text-[#007AFF] block mb-1" data-id="element-2405" data-mp="24qdqgjrc050x">
                              Waarom het werkt
                            </span>
                            <p className="text-sm text-[#6B7280] leading-relaxed" data-id="element-2406" data-mp="g6rekcro1dmt">
                              {comp.matters}
                            </p>
                          </div>
                        </div>

                        <div className="bg-gray-50 p-4 rounded-lg border border-gray-100" data-id="element-2407" data-mp="u08y3xkcqqcu">
                          <p className="text-sm text-gray-600 mb-2" data-id="element-2408" data-mp="yzc2rgwf6uka">
                            Wil je hier dieper in duiken?
                          </p>
                          <a href={comp.blogLink} className="text-sm font-semibold text-[#007AFF] hover:underline flex items-center" data-id="element-2409" data-mp="13wvf2b9k4x9l">
                            Lees: {comp.blogTitle}{' '}
                            <ArrowRightIcon className="w-3 h-3 ml-1" data-id="element-2410" data-mp="1cxtm72falkwe" />
                          </a>
                        </div>
                      </div>
                    </motion.div>}
                </AnimatePresence>
              </div>)}
          </div>
        </div>
      </section>

      {/* 6. PROGRAMMATIC SEO & 7. SEARCH EVERYWHERE */}
      <section className="py-20 bg-white" data-id="element-2411" data-mp="lxubs3l015uv">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-[1200px]" data-id="element-2412" data-mp="gxocq2cegh2r">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12" data-id="element-2413" data-mp="ux7b43fese1v">
            {/* Programmatic SEO */}
            <div className="bg-[#1B2B44] rounded-2xl p-8 md:p-10 text-white relative overflow-hidden" data-id="element-2414" data-mp="px34d54ayxou">
              <div className="absolute top-0 right-0 opacity-10 transform translate-x-1/4 -translate-y-1/4" data-id="element-2415" data-mp="14u28uk3uyaf3">
                <LayersIcon className="w-64 h-64" data-id="element-2416" data-mp="zupnk3smxbem" />
              </div>
              <div className="relative z-10" data-id="element-2417" data-mp="1dmxf9h52ac6g">
                <h3 className="text-2xl font-bold mb-4 flex items-center gap-3" data-id="element-2418" data-mp="1dmnq1vruc91t">
                  <DatabaseIcon className="w-6 h-6 text-[#007AFF]" data-id="element-2419" data-mp="19mn1hib12hh1" />
                  Programmatic SEO & Schalen
                </h3>
                <p className="text-white/80 leading-relaxed mb-6" data-id="element-2420" data-mp="1nj8v2h3v1k5n">
                  Zodra de basis staat, is het tijd om te schalen. Met
                  programmatic SEO genereren we op een slimme, kwalitatieve
                  manier tientallen tot honderden geoptimaliseerde pagina's.
                </p>
                <ul className="space-y-3 mb-8" data-id="element-2421" data-mp="274smcae24w8a">
                  <li className="flex items-start gap-3" data-id="element-2422" data-mp="1s8uie5xw6nli">
                    <CheckCircleIcon className="w-5 h-5 text-[#007AFF] flex-shrink-0 mt-0.5" data-id="element-2423" data-mp="1x8z0u1id1sed" />
                    <span className="text-white/90" data-id="element-2424" data-mp="1xas5jb9ey2jq">
                      <strong data-id="element-2425" data-mp="1jgskaebapjee">City pages:</strong> "Hypotheekadviseur [Stad]"
                      voor regionale dominantie.
                    </span>
                  </li>
                  <li className="flex items-start gap-3" data-id="element-2426" data-mp="19jwi0qbzptia">
                    <CheckCircleIcon className="w-5 h-5 text-[#007AFF] flex-shrink-0 mt-0.5" data-id="element-2427" data-mp="19ha50b6bylso" />
                    <span className="text-white/90" data-id="element-2428" data-mp="1egda1vmb8s0n">
                      <strong data-id="element-2429" data-mp="10igcavw4efke">Niche pages:</strong> Specifieke oplossingen voor
                      specifieke doelgroepen.
                    </span>
                  </li>
                </ul>
                <a href="/seo/blog/wat-is-programmatic-seo" className="inline-flex items-center text-white font-semibold hover:text-[#007AFF] transition-colors group" data-id="element-2430" data-mp="1l02kox1pidrf">
                  Lees hoe we schalen naar 100+ pagina's
                  <ArrowRightIcon className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" data-id="element-2431" data-mp="1b01xv14mpfih" />
                </a>
              </div>
            </div>

            {/* Search Everywhere */}
            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-8 md:p-10 border border-blue-100" data-id="element-2432" data-mp="122hvfov15d6a">
              <h3 className="text-2xl font-bold text-[#111827] mb-4 flex items-center gap-3" data-id="element-2433" data-mp="x85a199ztrrw">
                <GlobeIcon className="w-6 h-6 text-[#007AFF]" data-id="element-2434" data-mp="s9120lyt2035" />
                Search is overal
              </h3>
              <p className="text-[#6B7280] leading-relaxed mb-6" data-id="element-2435" data-mp="na7pavrced41">
                Mensen zoeken allang niet meer alleen via Google. Een modern SEO
                systeem zorgt dat je overal vindbaar bent waar jouw doelgroep
                zoekt.
              </p>
              <div className="grid grid-cols-2 gap-4 mb-8" data-id="element-2436" data-mp="2py65wr91awex">
                <div className="bg-white p-4 rounded-xl border border-gray-100 shadow-sm flex items-center gap-3" data-id="element-2437" data-mp="2tzrp0uc5w6xk">
                  <SearchIcon className="w-5 h-5 text-gray-400" data-id="element-2438" data-mp="2z0fidmnv4zuy" />
                  <span className="font-semibold text-gray-800" data-id="element-2439" data-mp="33y8lu7xs7frj">Google</span>
                </div>
                <div className="bg-white p-4 rounded-xl border border-gray-100 shadow-sm flex items-center gap-3" data-id="element-2440" data-mp="26b05ssgjuywx">
                  <NetworkIcon className="w-5 h-5 text-blue-600" data-id="element-2441" data-mp="2b9a256e0g5k4" />
                  <span className="font-semibold text-gray-800" data-id="element-2442" data-mp="2b7grzh46v37d">LinkedIn</span>
                </div>
                <div className="bg-white p-4 rounded-xl border border-gray-100 shadow-sm flex items-center gap-3" data-id="element-2443" data-mp="2g735lrnxilgp">
                  <PlayCircleIcon className="w-5 h-5 text-red-500" data-id="element-2444" data-mp="1nkiemsbd8bdo" />
                  <span className="font-semibold text-gray-800" data-id="element-2445" data-mp="1ni4nuvnpxlbx">YouTube</span>
                </div>
                <div className="bg-white p-4 rounded-xl border border-gray-100 shadow-sm flex items-center gap-3" data-id="element-2446" data-mp="1sh8v3gr318zm">
                  <BrainIcon className="w-5 h-5 text-purple-500" data-id="element-2447" data-mp="178hhirshnyud" />
                  <span className="font-semibold text-gray-800" data-id="element-2448" data-mp="177zffej5aqej">AI Tools</span>
                </div>
              </div>
              <a href="/seo/blog/seo-voor-ai-tools" className="inline-flex items-center text-[#007AFF] font-semibold hover:text-[#005FCC] transition-colors group" data-id="element-2449" data-mp="taszdrpt35t0">
                Lees meer over SEO voor AI tools
                <ArrowRightIcon className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" data-id="element-2450" data-mp="o9n0td9gsobk" />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* 8. VISUAL FORMULAS */}
      <section className="py-20 bg-[#111827] text-white" data-id="element-2451" data-mp="ejajrv0uzda6">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-[1200px]" data-id="element-2452" data-mp="ehr2q27doy7c">
          <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-2453" data-mp="kko8jtotlnx">
            <h2 className="text-3xl md:text-4xl font-bold mb-6" data-id="element-2454" data-mp="5jnjxdns2fj8">
              De wiskunde achter online succes
            </h2>
            <p className="text-lg text-gray-400 leading-relaxed mb-6" data-id="element-2455" data-mp="3i3j94ow3yy6p">
              SEO is geen magie. Het is een logisch gevolg van de juiste
              variabelen.
            </p>
            <div className="flex items-center justify-center gap-3" data-id="element-2456" data-mp="3r4h9mx7c8nkj">
              <img src="https://cdn.magicpatterns.com/uploads/fv5nFyNt1SPvWUkxd2YKYJ/Norden_portret_300dpi_2.png" alt="Norden" className="w-6 h-6 rounded-full border border-gray-600" data-id="element-2457" data-mp="3n22rygfentuu" />
              <p className="text-sm italic text-gray-400" data-id="element-2458" data-mp="3w2ivfpw5f0zp">
                Norden rekent het voor je uit.
              </p>
            </div>
          </div>

          <div className="flex flex-col gap-4 max-w-4xl mx-auto" data-id="element-2459" data-mp="elhrjo0ua93c">
            {formulas.map((item, idx) => <div key={idx} className="bg-white/5 border border-white/10 rounded-xl p-6 flex flex-col md:flex-row items-start md:items-center justify-between gap-4 hover:bg-white/10 transition-colors" data-id="element-2460" data-mp="9o9axrvcad47" data-mpi={idx}>
                <span className="text-gray-400 font-medium text-sm uppercase tracking-wider" data-id="element-2461" data-mp="ip7ds6u1kel7">
                  {item.title}
                </span>
                <span className="text-xl md:text-2xl font-bold text-white font-mono tracking-tight" data-id="element-2462" data-mp="3heflwqt0t14k">
                  {item.formula}
                </span>
              </div>)}
          </div>
        </div>
      </section>

      {/* 9. FAQ SECTION */}
      <section className="py-20 bg-[#F7F8FA]" data-id="element-2463" data-mp="3q8k8uz519set">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-[800px]" data-id="element-2464" data-mp="3v6cawlze6uwq">
          <div className="text-center mb-12" data-id="element-2465" data-mp="3v8gc2w5wpftb">
            <h2 className="text-3xl font-bold text-[#111827] mb-4" data-id="element-2466" data-mp="3yq10bm2xw7f">
              Veelgestelde vragen
            </h2>
            <p className="text-[#6B7280] mb-6" data-id="element-2467" data-mp="8zdq2rqr02z0">
              Eerlijke antwoorden op de meest gestelde vragen over SEO.
            </p>
            <div className="flex items-center justify-center gap-3" data-id="element-2468" data-mp="cy4px6mllypt">
              <img src="https://cdn.magicpatterns.com/uploads/fv5nFyNt1SPvWUkxd2YKYJ/Norden_portret_300dpi_2.png" alt="Norden" className="w-6 h-6 rounded-full border border-gray-300" data-id="element-2469" data-mp="2artm9ptyafbi" />
              <p className="text-sm italic text-[#6B7280]" data-id="element-2470" data-mp="27932rl3s1cm0">
                De vragen die Norden het vaakst hoort van financieel adviseurs.
              </p>
            </div>
          </div>

          <div className="space-y-4" data-id="element-2471" data-mp="1xjjjwfg2dir5">
            {faqs.map((faq, idx) => <div key={idx} className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-id="element-2472" data-mp="1th6fpvgytswh" data-mpi={idx}>
                <button onClick={() => setExpandedFaq(expandedFaq === idx ? null : idx)} className="w-full px-6 py-5 flex items-center justify-between text-left focus:outline-none" data-id="element-2473" data-mp="2q6xalvt65i2t">
                  <span className="font-bold text-[#111827] pr-8" data-id="element-2474" data-mp="2l69mbhwbrfca">{faq.q}</span>
                  {expandedFaq === idx ? <ChevronUpIcon className="w-5 h-5 text-[#007AFF] flex-shrink-0" data-id="element-2475" data-mp="2c7eek1dmtx6v" /> : <ChevronDownIcon className="w-5 h-5 text-gray-400 flex-shrink-0" data-id="element-2476" data-mp="2c74mm8911fyj" />}
                </button>
                <AnimatePresence data-id="element-2477" data-mp="15p93i39lsyuu">
                  {expandedFaq === idx && <motion.div initial={{
                height: 0,
                opacity: 0
              }} animate={{
                height: 'auto',
                opacity: 1
              }} exit={{
                height: 0,
                opacity: 0
              }} className="overflow-hidden" data-id="element-2478" data-mp="10r7lzmrtt7lh">
                      <div className="px-6 pb-6 pt-2 text-[#6B7280] leading-relaxed border-t border-gray-50" data-id="element-2479" data-mp="2fpjy17dnx3an">
                        {faq.a}
                      </div>
                    </motion.div>}
                </AnimatePresence>
              </div>)}
          </div>
        </div>
      </section>

      {/* 9.5 BRIDGE SECTION */}
      <section className="py-24 bg-gradient-to-r from-[#007AFF] to-[#005FCC] text-white" data-id="element-2480" data-mp="3ccf5idbn9m6s">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-[800px] text-center" data-id="element-2481" data-mp="2yegv1j0g29wk">
          <img src="https://cdn.magicpatterns.com/uploads/fv5nFyNt1SPvWUkxd2YKYJ/Norden_portret_300dpi_2.png" alt="Norden" className="w-16 h-16 rounded-full border-2 border-white/30 mx-auto mb-6" data-id="element-2482" data-mp="33ca1md5nt5xd" />
          <h2 className="text-3xl md:text-4xl font-bold mb-6" data-id="element-2483" data-mp="1s0952c2k0d72">
            De vraag is niet óf je SEO nodig hebt, maar waar je nu staat.
          </h2>
          <p className="text-xl text-white/80 mb-10" data-id="element-2484" data-mp="1fglmgxgpmyie">
            Doe de scan en ontdek in 2 minuten waar jouw grootste groeikansen
            liggen.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4" data-id="element-2485" data-mp="1tdqyde33urlk">
            <a href="/prijsscan" className="inline-flex justify-center items-center px-8 py-4 bg-white text-[#007AFF] font-bold rounded-lg hover:bg-gray-50 transition duration-300 shadow-lg group" data-id="element-2486" data-mp="1oh9zppya4uu8">
              Start de SEO scan
              <ArrowRightIcon className="h-5 w-5 ml-2 group-hover:translate-x-1 transition-transform" data-id="element-2487" data-mp="vq8i7i746n7j" />
            </a>
            <a href="/#contact" className="inline-flex justify-center items-center px-8 py-4 bg-transparent border-2 border-white/30 text-white font-bold rounded-lg hover:bg-white/10 transition duration-300" data-id="element-2488" data-mp="15qivf2t5fump">
              Plan een strategiegesprek
            </a>
          </div>
        </div>
      </section>

      {/* 10. BLOG ARTICLES OVERVIEW */}
      <section className="py-24 bg-white" data-id="element-2489" data-mp="2d3bg41nrgwdn">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-[1200px]" data-id="element-2490" data-mp="2i2o2ymemu3g8">
          <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-2491" data-mp="2i0l55g78n60y">
            <h2 className="text-3xl md:text-4xl font-bold text-[#111827] mb-6" data-id="element-2492" data-mp="2mu653dse6a2s">
              Duik dieper in de materie
            </h2>
            <p className="text-lg text-[#6B7280] leading-relaxed" data-id="element-2493" data-mp="2qx2jstet9m1g">
              Onze complete kennisbank. Geen oppervlakkige tips, maar diepgaande
              strategische artikelen over hoe SEO écht werkt.
            </p>
          </div>

          {/* Featured Article */}
          <div className="mb-16 bg-[#F7F8FA] rounded-3xl p-8 md:p-12 border border-gray-200" data-id="element-2494" data-mp="234c7gj2zx0qn">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center" data-id="element-2495" data-mp="2861ja0y1lifs">
              <div data-id="element-2496" data-mp="3ek0utbk5w1rz">
                <span className="inline-block px-3 py-1 bg-blue-100 text-[#007AFF] text-sm font-bold rounded-full mb-4 uppercase tracking-wider" data-id="element-2497" data-mp="3ekka0kifgc2b">
                  Cornerstone Artikel
                </span>
                <h3 className="text-3xl font-bold text-[#111827] mb-4" data-id="element-2498" data-mp="3tpy63kxcwd4z">
                  Wat is een SEO silo en hoe werkt het?
                </h3>
                <p className="text-[#6B7280] text-lg leading-relaxed mb-8" data-id="element-2499" data-mp="3jx8y5010lhee">
                  Dit is het belangrijkste artikel in onze kennisbank. Ontdek
                  waarom 90% van de websites faalt in Google en hoe je met een
                  strakke silo-structuur de concurrentie domineert.
                </p>
                <a href="/seo/blog/seo-silo-wat-is-het" className="inline-flex items-center bg-[#1B2B44] text-white px-6 py-3 rounded-lg font-semibold hover:bg-[#111827] transition-colors group" data-id="element-2500" data-mp="35xhmv71vuf43">
                  Lees het cornerstone artikel
                  <ArrowRightIcon className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" data-id="element-2501" data-mp="z7tbmh8tweuk" />
                </a>
              </div>
              <div className="relative hidden lg:block" data-id="element-2502" data-mp="v7j7zb8ie0iv">
                <div className="absolute inset-0 bg-gradient-to-tr from-[#007AFF]/20 to-transparent rounded-2xl transform rotate-3" data-id="element-2503" data-mp="1eeyazouuqlz1"></div>
                <div className="bg-white p-8 rounded-2xl border border-gray-200 relative z-10 shadow-lg" data-id="element-2504" data-mp="1ifi6yp3ovjjl">
                  <div className="w-full h-40 bg-gray-50 rounded-lg border-2 border-dashed border-gray-200 flex items-center justify-center mb-6" data-id="element-2505" data-mp="14odcs2j3bjgn">
                    <NetworkIcon className="w-12 h-12 text-gray-300" data-id="element-2506" data-mp="uqyxi68wl0px" />
                  </div>
                  <div className="h-4 bg-gray-100 rounded w-3/4 mb-3" data-id="element-2507" data-mp="zq225pkz14xd"></div>
                  <div className="h-4 bg-gray-100 rounded w-1/2" data-id="element-2508" data-mp="zrd6prm8qgpj"></div>
                </div>
              </div>
            </div>
          </div>

          {/* Clusters Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-2509" data-mp="qq6n39oaops2">
            {blogClusters.map((cluster, idx) => <div key={idx} className="bg-white rounded-2xl p-6 border border-gray-200 shadow-sm hover:shadow-md transition-shadow" data-id="element-2510" data-mp="2g53a0w5jd0y4" data-mpi={idx}>
                <div className="flex items-center justify-between mb-4 pb-4 border-b border-gray-100" data-id="element-2511" data-mp="2v7cx8lw21i3s">
                  <h3 className="text-lg font-bold text-[#1B2B44] flex items-center gap-2" data-id="element-2512" data-mp="2q8ifln9tpzl9">
                    <span className="w-6 h-6 rounded-full bg-blue-50 text-[#007AFF] flex items-center justify-center text-xs" data-id="element-2513" data-mp="33zn907mllvn5">
                      {idx + 1}
                    </span>
                    {cluster.title}
                  </h3>
                  <span className="text-xs font-semibold text-gray-400 bg-gray-50 px-2 py-1 rounded-md" data-id="element-2514" data-mp="3dwjgqb4oadqn">
                    {cluster.articles.length} artikelen
                  </span>
                </div>
                <ul className="space-y-3" data-id="element-2515" data-mp="3mxzftmq1etfk">
                  {cluster.articles.map((article, aIdx) => <li key={aIdx} data-id="element-2516" data-mp="3ixz449ubowc7" data-mpi={aIdx}>
                      <a href={`/seo/blog/${article.slug}`} className="text-sm text-[#4B5563] hover:text-[#007AFF] hover:underline flex items-start gap-2 leading-snug group" data-id="element-2517" data-mp="3iutbwlqedec9">
                        <ChevronRightIcon className="w-4 h-4 mt-0.5 flex-shrink-0 text-gray-300 group-hover:text-[#007AFF] transition-colors" data-id="element-2518" data-mp="1okhb47b5f79s" />
                        {article.title}
                      </a>
                    </li>)}
                </ul>
              </div>)}
          </div>

          {/* Meer artikelen CTA */}
          <div className="mt-16 text-center" data-id="element-2519" data-mp="15ou6r9kk5jfx">
            <a href="/bibliotheek" className="inline-flex items-center bg-white border-2 border-[#1B2B44] text-[#1B2B44] hover:bg-[#1B2B44] hover:text-white font-bold py-4 px-8 rounded-lg transition duration-300 shadow-sm hover:shadow-md group" data-id="element-2520" data-mp="10neil8n32wtb">
              Bekijk alle artikelen in de Bibliotheek
              <ArrowRightIcon className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" data-id="element-2521" data-mp="lz0ekudxl2b6" />
            </a>
          </div>
        </div>
      </section>

      {/* 11. FINAL CTA */}
      <section className="py-24 bg-gradient-to-br from-[#007AFF] to-[#1B2B44] relative overflow-hidden" data-id="element-2522" data-mp="1xd5o02wwy4dw">
        <div className="absolute inset-0 opacity-10" data-id="element-2523" data-mp="1td3j7kvvsuue">
          <div className="w-full h-full bg-[url('https://images.unsplash.com/photo-1551434678-e076c223a692?ixlib=rb-4.0.3&auto=format&fit=crop&w=1470&q=80')] bg-cover bg-center" data-id="element-2524" data-mp="1teoe5bm3yfss"></div>
        </div>

        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-[800px] relative z-10 text-center" data-id="element-2525" data-mp="25sl4minag6me">
          <img src="https://cdn.magicpatterns.com/uploads/fv5nFyNt1SPvWUkxd2YKYJ/Norden_portret_300dpi_2.png" alt="Norden mascotte" className="w-24 h-24 rounded-full border-4 border-white/20 mx-auto mb-8 shadow-xl" data-id="element-2526" data-mp="1r01ced1ofjjq" />

          <h2 className="text-3xl md:text-5xl font-bold text-white mb-6 leading-tight" data-id="element-2527" data-mp="2ol5qvh8hfdh8">
            SEO is geen losse dienst. Het is een systeem dat moet kloppen.
          </h2>

          <p className="text-xl text-white/80 mb-10 leading-relaxed" data-id="element-2528" data-mp="29uoqm17l2tlx">
            Klaar om te stoppen met losse flodders en te starten met structurele
            groei? Laten we kijken waar jouw kansen liggen.
          </p>

          <div className="flex flex-col sm:flex-row justify-center gap-4" data-id="element-2529" data-mp="37cgjc2tuvyb8">
            <a href="/prijsscan" className="inline-flex justify-center items-center px-8 py-4 bg-white text-[#007AFF] font-bold rounded-lg hover:bg-gray-50 transition duration-300 shadow-lg group" data-id="element-2530" data-mp="3h7x54wfqw3yn">
              Start de scan
              <ArrowRightIcon className="h-5 w-5 ml-2 group-hover:translate-x-1 transition-transform" data-id="element-2531" data-mp="386fsxp5gkmf8" />
            </a>
            <a href="/#contact" className="inline-flex justify-center items-center px-8 py-4 bg-transparent border-2 border-white/30 text-white font-bold rounded-lg hover:bg-white/10 transition duration-300" data-id="element-2532" data-mp="337cmc09ty855">
              Plan een gesprek
            </a>
          </div>
        </div>
      </section>

      <Footer data-id="element-2533" data-mp="2ok1cc2taqw75" />
    </div>;
};